package org.teddapp.project.ui.today

import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.tedd.db.Finance_account
import com.tedd.db.Finance_transaction
import com.tedd.db.Craving_observation
import com.tedd.db.Flashcard
import com.tedd.db.Loan
import com.tedd.db.Loan_payment
import com.tedd.db.Skill
import com.tedd.db.Focus_session
import com.tedd.db.Task
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.launch
import org.teddapp.project.db.TeddRepositories
import org.teddapp.project.ui.TeddSnackbarState
import org.teddapp.project.ui.finance.MONTH_END
import org.teddapp.project.ui.finance.MONTH_START
import org.teddapp.project.ui.finance.rationalRate
import org.teddapp.project.ui.focus.FOCUS_TIMER_DEFAULT_MIN
import org.teddapp.project.ui.pomodoro.POM_TODAY_ISO
import org.teddapp.project.ui.rememberTeddSnackbarState

/**
 * État d'Aujourd'hui, partagé par les mises en page compacte et étendue.
 *
 * Les listes brutes sont alimentées par [rememberTodayModel] ; tout le reste est dérivé
 * à la lecture (les volumes restent petits : pool de 6 tâches, un mois de transactions).
 */
/** La seule chose proposée en tête d'Aujourd'hui. */
internal data class Recommendation(val task: Task, val minutes: Int, val context: String)

internal class TodayModel(
    private val repos: TeddRepositories,
    private val scope: CoroutineScope,
    val snackbar: TeddSnackbarState,
) {
    // ── Données ────────────────────────────────────────────────────
    var tasks by mutableStateOf<List<Task>>(emptyList())
    var accounts by mutableStateOf<List<Finance_account>>(emptyList())
    var txns by mutableStateOf<List<Finance_transaction>>(emptyList())
    var focusSessions by mutableStateOf<List<Focus_session>>(emptyList())
    var cravings by mutableStateOf<List<Craving_observation>>(emptyList())
    var flashcards by mutableStateOf<List<Flashcard>>(emptyList())
    var skills by mutableStateOf<List<Skill>>(emptyList())
    var loans by mutableStateOf<List<Loan>>(emptyList())
    var loanPayments by mutableStateOf<List<Loan_payment>>(emptyList())

    // ── Dérivés ────────────────────────────────────────────────────
    val pool: List<Task> get() = focusPool(tasks)
    val doneToday: Int
        get() = tasks.count { it.is_deleted == 0L && it.status == "done" && it.due_date == TODAY_HOME_ISO }
    val dayScore: Int get() = computeDayScore(pool.size, doneToday)
    val suggestion: Task? get() = pool.firstOrNull()

    val courant: Finance_account?
        get() = accounts.firstOrNull { it.id == "acc_courant" } ?: accounts.firstOrNull()
    val monthStats: Pair<Long, Long> get() = monthFinanceStats(txns, courant?.id ?: "acc_courant")
    val balance30: List<Long> get() = balanceLastDays(30, courant, txns)

    val focusMin7: List<Int> get() = focusMinutesLastDays(7, focusSessions)
    val weekLabels: List<String> = weekdayLabelsFor7DaysEnding()

    // ── Les trois nombres du jour (v2) ─────────────────────────────
    /** Minutes de focus enregistrées aujourd'hui, écrites par le minuteur. */
    val focusMinutesToday: Int get() = focusMin7.lastOrNull() ?: 0

    /** Cartes de révision dues aujourd'hui. */
    val cardsDue: Int
        get() = flashcards.count { it.is_deleted == 0L && it.due_date <= TODAY_HOME_ISO }

    /** Envies traversées aujourd'hui (pilier Habitudes). */
    val crossingsToday: Int
        get() = cravings.count { it.is_deleted == 0L && it.date == TODAY_HOME_ISO && it.outcome == "crossed" }

    /** Taux de décisions rationnelles du mois, ou null si aucune dépense majeure. */
    val rationalRateMonth: Int?
        get() = rationalRate(txns.filter { it.is_deleted == 0L && it.date in MONTH_START..MONTH_END }.map { it.verdict })

    /**
     * La chose à faire maintenant. L'agent du noyau la calculera ; d'ici là, la première tâche
     * du pool focus, avec sa durée estimée si elle en a une.
     */
    val recommendation: Recommendation?
        get() = suggestion?.let { task ->
            Recommendation(
                task = task,
                minutes = task.estimated_minutes?.toInt() ?: FOCUS_TIMER_DEFAULT_MIN,
                context = task.source().label,
            )
        }

    // ── Capture rapide ─────────────────────────────────────────────
    var quickOpen by mutableStateOf(false)
    var quickDraft by mutableStateOf("")

    fun submitQuick() {
        val title = quickDraft.trim()
        if (title.isEmpty()) return
        scope.launch {
            repos.pomodoro.addQuick(POM_TODAY_ISO, title)
            snackbar.show("Ajouté au focus du jour")
        }
        quickDraft = ""
    }

    fun toggleTask(task: Task) {
        scope.launch { repos.tasks.toggleDone(task) }
    }
}

@Composable
internal fun rememberTodayModel(repos: TeddRepositories): TodayModel {
    val scope = rememberCoroutineScope()
    val snackbar = rememberTeddSnackbarState()
    val model = remember(repos) { TodayModel(repos, scope, snackbar) }
    model.tasks = repos.tasks.observeAll().collectAsState(emptyList()).value
    model.accounts = repos.financeAccounts.observeAll().collectAsState(emptyList()).value
    model.txns = repos.financeTxns.observeAll().collectAsState(emptyList()).value
    model.focusSessions = repos.focusSessions.observeAll().collectAsState(emptyList()).value
    model.cravings = repos.cravings.observeAll().collectAsState(emptyList()).value
    model.flashcards = repos.flashcards.observeAll().collectAsState(emptyList()).value
    model.skills = repos.skills.observeAll().collectAsState(emptyList()).value
    model.loans = repos.loans.observeAll().collectAsState(emptyList()).value
    model.loanPayments = repos.loans.observeAllPayments().collectAsState(emptyList()).value
    return model
}
